import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { useTheme } from 'react-admin';

import { GrowthOnSalesReport, reportsHttp } from '@network';
import { InitialCard } from '@components';

import cls from './growth-on-sales.module.css';

const DATE_FORMAT = 'MMM – yyyy';

interface Props {
  range: string;
  setLoader: (num: number, value: boolean) => void;
}

export const GrowthOnSales = ({ range, setLoader }: Props) => {
  const [numbers, setNumbers] = useState<GrowthOnSalesReport[]>([]);
  const [currentTheme] = useTheme();
  const chartRef = useRef<HTMLDivElement | null>(null);

  const id = 'growth-on-sales';

  useEffect(() => {
    const loadNumbers = async () => {
      setLoader(7, true);
      try {
        const nextNumbers = await reportsHttp.growthOnSales({ range });
        setNumbers(nextNumbers);
      } catch (e) {
        console.error(e);
      } finally {
        setLoader(7, false);
      }
    };
    void loadNumbers();
  }, [range]);

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);

    root.setThemes([
      am5themes_Animated.new(root),
    ]);

    const labelColor = am5.color(currentTheme === 'light' ? '#000000' : '#ffffff');

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
      }),
    );

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: 'month', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {}),
        dateFormats: {
          month: DATE_FORMAT,
        },
        periodChangeDateFormats: {
          month: DATE_FORMAT,
        },
        tooltipDateFormat: DATE_FORMAT,
      }),
    );
    xAxis.get('renderer').labels.template.setAll({ fill: labelColor });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        numberFormatter: am5.NumberFormatter.new(root, {
          numberFormat: '#.#a',
          bigNumberPrefixes: [
            { number: 1e3, suffix: 'K' },
            { number: 1e6, suffix: 'M' },
            { number: 1e9, suffix: 'B' },
          ],
        }),
      }),
    );
    yAxis.get('renderer').labels.template.setAll({ fill: labelColor });

    const values = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Growth on sales',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        valueXField: 'date',
        stroke: am5.color('#042E6B'),
      }),
    );
    values.bullets.push(() => {
      return am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 5,
          fill: values.get('stroke'),
        }),
      });
    });
    values.strokes.template.setAll({
      strokeWidth: 2,
      stroke: am5.color('#042E6B'),
    });
    values.data.setAll(numbers);

    const averageValues = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Average Credit Amount',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'averageValue',
        valueXField: 'date',
        stroke: am5.color('#FD8A32'),
      }),
    );
    averageValues.bullets.push(() => {
      return am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 5,
          fill: averageValues.get('stroke'),
        }),
      });
    });
    averageValues.strokes.template.setAll({
      strokeWidth: 2,
      stroke: am5.color('#FD8A32'),
    });
    averageValues.data.setAll(numbers);

    chart.set('cursor', am5xy.XYCursor.new(root, {
      xAxis: xAxis,
      snapToSeries: [values, averageValues],
    }));

    values.set('tooltip', am5.Tooltip.new(root, {
      labelText: '{valueY.formatNumber("#.#a")}',
      numberFormatter: am5.NumberFormatter.new(root, {
        numberFormat: '#.#a',
        bigNumberPrefixes: [
          { number: 1e3, suffix: 'K' },
          { number: 1e6, suffix: 'M' },
          { number: 1e9, suffix: 'B' },
        ],
      }),
    }));
    averageValues.set('tooltip', am5.Tooltip.new(root, {
      labelText: '{valueY.formatNumber("#.#a")}',
      numberFormatter: am5.NumberFormatter.new(root, {
        numberFormat: '#.#a',
        bigNumberPrefixes: [
          { number: 1e3, suffix: 'K' },
          { number: 1e6, suffix: 'M' },
          { number: 1e9, suffix: 'B' },
        ],
      }),
    }));

    chart.set('layout', root.verticalLayout);

    const legend = chart.children.push(am5.Legend.new(root, {
      centerX: am5.percent(50),
      x: am5.percent(50),
      marginTop: 40,
    }));
    legend.labels.template.setAll({ fill: labelColor });
    legend.data.setAll(chart.series.values);

    values.set('fill', am5.color('#042E6B'));
    averageValues.set('fill', am5.color('#FD8A32'));

    return () => {
      root.dispose();
    };
  }, [id, numbers, currentTheme]);

  return (
    <InitialCard left="Sales' Growth and Average Credit Amount" extraPadding wrapperClass={cls.wrap}>
      <div
        id={id}
        ref={chartRef}
        className={cls.chart}
      />
    </InitialCard>
  );
};
